import * as React from 'react';
import Cookies from 'js-cookie';
import { CookieConsentContext } from './cookie-consent-context';
import type { ConsentCategories } from '@/lib/consent/gtag';
import { updateConsent } from '@/lib/consent/gtag';

function useCookieContext() {
  const context = React.useContext(CookieConsentContext);
  if (!context) {
    throw new Error(
      'useCookieConsent must be used within a CookieConsentProvider',
    );
  }
  return context;
}
export function useCookies() {
  const setCookie = (
    name: string,
    value: string,
    options?: Cookies.CookieAttributes,
  ) => {
    Cookies.set(name, value, options);
  };

  const getCookie = (name: string) => {
    return Cookies.get(name);
  };

  const removeCookie = (name: string, options?: Cookies.CookieAttributes) => {
    Cookies.remove(name, options);
  };

  return {
    setCookie,
    getCookie,
    removeCookie,
  };
}

const isProduction = process.env.NODE_ENV === 'production';

const cookieOptions = {
  expires: 180, // 6 months as per GDPR requirements
  sameSite: 'strict',
  secure: isProduction,
} as const;

export function useCookieConsent() {
  const { consentCategories, updateConsentCategories } = useCookieContext();
  const { setCookie } = useCookies();

  const acceptAll = () => {
    const categories = {
      functionality: 'granted' as const,
      analytics: 'granted' as const,
      ads: 'granted' as const,
    };
    setCookie('consent', JSON.stringify(categories), cookieOptions);
    updateConsentCategories(categories);

    // Update Google Consent Mode
    updateConsent(categories);
  };

  const acceptRequired = () => {
    const categories = {
      functionality: 'granted' as const,
      analytics: 'denied' as const,
      ads: 'denied' as const,
    };
    setCookie('consent', JSON.stringify(categories), cookieOptions);
    updateConsentCategories(categories);

    // Update Google Consent Mode
    updateConsent(categories);
  };

  const rejectAll = () => {
    const categories = {
      functionality: 'granted' as const,
      analytics: 'denied' as const,
      ads: 'denied' as const,
    };
    setCookie('consent', JSON.stringify(categories), cookieOptions);
    updateConsentCategories(categories);

    // Update Google Consent Mode
    updateConsent(categories);
  };

  return {
    consentCategories,
    acceptAll,
    acceptRequired,
    rejectAll,
    setConsentCategories: (categories: ConsentCategories | null) => {
      if (categories) {
        setCookie('consent', JSON.stringify(categories), cookieOptions);
        updateConsentCategories(categories);

        // Update Google Consent Mode
        updateConsent(categories);
      }
    },
  };
}
